import { useState } from 'react'
import icon from '@/assets/icons/icon.png'
import { Eye, EyeOff } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useBaseStore } from '@/store'
import { useMutation } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { authenticateUser } from '@/api/auth/user'
import { AxiosError } from 'axios'
import { useToast } from '@/components/ui/use-toast'
import Button from '@/components/Button'
import { decodeJwt } from 'jose'
import { Token } from '@/api/auth/user/type'
import { Store } from '@/store/type'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod' 
import { getHeaders } from '@/api/utils'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { TypographyH3, TypographyMuted, TypographyP } from '@/components/ui/typography'
import { Input } from '@/components/ui/input'

const stateSelector = (state: Store) => ({
    login: state.authSlice.actions.login,
})

const loginSchema = z.object({
    username: z.string().min(1, 'Campo obrigatório'),
    password: z.string().min(1, 'Campo obrigatório'),
})

type LoginSchemaType = z.infer<typeof loginSchema>

const Login = () => {
    const [passwordVisible, setPasswordVisible] = useState(false)

    const { toast } = useToast()
    const { state } = useLocation()
    const navigate = useNavigate()

    const { login } = useBaseStore(stateSelector)

    const { mutate: onLogin, isPending } = useMutation({
        mutationFn: authenticateUser,
        onSuccess: (data) => {
            const decoded = decodeJwt(data.token) as Token

            login({
                token: data.token,
                user: decoded.usuario.nome,
                userId: decoded.usuario.cod_usuario,
            })

            navigate(state?.path || '/')
        },
        onError: (err: AxiosError) => {
            if (isAxiosError(err)) {
                toast({
                    title: 'Erro ao fazer login',
                    description: 'Usuário ou senha incorretos',
                    variant: 'destructive',
                })
            }
        },
    })

    const form = useForm<LoginSchemaType>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            password: '',
            username: '',
        },
    })

    const onSubmit = async (data: LoginSchemaType) => {
        onLogin({ user: data.username, password: data.password })
    }

    return (
        <Form {...form}>
            <form
                className="absolute top-1/2 left-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 transform flex-col gap-4 p-4 sm:w-[480px] sm:p-0"
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <div className="flex items-center gap-2 mx-auto">
                    <img
                        src={icon}
                        className="w-8 h-8 md:w-11 md:h-11"
                        alt="Portal icon"
                    />
                    <TypographyH3 className="text-white">COCKPIT</TypographyH3>
                </div>
                <FormField
                    name="username"
                    control={form.control}
                    render={({ field }) => {
                        return (
                            <FormItem>
                                <FormLabel className='text-white'>Usuário</FormLabel>
                                <FormControl>
                                    <Input {...field} className='w-full bg-gray-600 text-sm rounded-md px-4 py-2 text-gray-50 outline-none focus:border-primary-500 border-none' />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )
                    }}
                />
                <FormField
                    name="password"
                    control={form.control}
                    render={({ field }) => {
                        return (
                            <FormItem>
                                <FormLabel className='text-white'>Senha</FormLabel>
                                <FormControl>
                                    <div className="flex items-center rounded-md text-sm  bg-gray-600 pr-2  py-2 focus-within:border-primary-500">
                                        <Input
                                            {...field}
                                            type={
                                                passwordVisible
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            className='flex-1 h-full pr-2 bg-transparent outline-none text-gray-50 border-none'
                                        />
                                        {passwordVisible ? (
                                            <EyeOff
                                                className="cursor-pointer text-white hover:text-neutral-500"
                                                onClick={() =>
                                                    setPasswordVisible(
                                                        (prev) => !prev
                                                    )
                                                }
                                                size={24}
                                            />
                                        ) : (
                                            <Eye
                                                className="cursor-pointer text-white hover:text-neutral-500"
                                                onClick={() =>
                                                    setPasswordVisible(
                                                        (prev) => !prev
                                                    )
                                                }
                                                size={24}
                                            />
                                        )}
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )
                    }}
                />
                <TypographyMuted asChild className="text-end hover:underline">
                    <Link to="/recuperar-senha" className='text-gray-300'>Recuperar senha</Link>
                </TypographyMuted>

                <Button className="w-full md:py-3" isLoading={isPending}>
                    Entrar
                </Button>

                <div className="overflow-hidden">
                    <TypographyP className=" text-center text-gray-300">
                        V{getHeaders()['x-versao']}
                    </TypographyP>
                </div>
            </form>
        </Form>
    )
}

export default Login
